import React, { useState, useEffect } from 'react'
import classnames from 'classnames'
import truncate from 'lodash/truncate'
import { Box } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme'

export interface IReadMoreProps {
  /**
   * This will be the main text to be displayed and hidden
   */
  mainText: string
  /**
   * Maximum length of string that will be displayed in the contracted state
   * @default 45
   */
  maxLength?: number
  /**
   * Overrides the current state of this element
   * @default false
   */
  isExpanded?: boolean
  /**
   * Overrides the default text for the expand button
   * @default 'View More'
   */
  viewMoreText?: string
  /**
   * Overrides the default text for the collapse button
   * @default 'View Less'
   */
  viewLessText?: string
  /**
   * Overrides the default text breaking of break-all to normal
   * @default 'false'
   */
  noWordBreak?: boolean
}

const useReadMoreStyles = makeStyles({
  expander: {
    color: brandColors.skyBlue6,
    fontStyle: 'normal',
    width: '100%',
    display: 'flex',
    cursor: 'pointer',
  },
  content: {
    color: brandColors.coolGray8,
    wordBreak: 'break-all',
  },
  noWordBreak: {
    wordBreak: 'normal',
  },
})

/**
 * @description This component receives two values
 * first the main text that will be the text that have the view more and view less functionality
 * the second is the maxLength, that will be the length of the amount of text to show, when the main text isnt looking complete (Default State)
 * @example <ReadMore mainText="This is some text we want to show" maxLength={45} />
 */
export const ReadMore = ({
  isExpanded: isExpandedProp = false,
  mainText,
  maxLength = 45,
  noWordBreak = false,
  viewLessText = 'View Less',
  viewMoreText = 'View More',
}: IReadMoreProps) => {
  const classes = useReadMoreStyles()
  const [isExpanded, setExpanded] = useState<boolean>(isExpandedProp)

  const toggleHandler = () => setExpanded(prevState => !prevState)

  // if this prop updates, update the internal state too
  useEffect(() => {
    setExpanded(isExpandedProp)
  }, [isExpandedProp])

  const mainTextLength = mainText?.length
  return (
    <Box display='flex' overflow='hidden' data-test='read-more'>
      <Box
        className={classnames(classes.content, {
          [classes.noWordBreak]: noWordBreak,
        })}>
        <Box>
          {isExpanded ? mainText : truncate(mainText, { length: maxLength })}
          {mainTextLength > maxLength && (
            <span
              className={classes.expander}
              role='button'
              tabIndex={-1}
              onClick={toggleHandler}
              onKeyDown={toggleHandler}>
              {isExpanded ? viewLessText : viewMoreText}
            </span>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default ReadMore
